import { PART_COLORS } from "./ProductGallery";
import { Eye, EyeOff } from "lucide-react";
import { useState } from "react";
import ContactModal from "./ContactModal";

interface ProductInfoProps {
  product: any;
  selectedParts: string[];
  onPartsChange: (parts: string[]) => void;
  partColors: {
    AB: string;
    ORINGS: string;
  };
  onPartColorChange: (key: "AB" | "ORINGS", color: string) => void;
}

const ProductInfo: React.FC<ProductInfoProps> = ({
  product,
  selectedParts,
  onPartsChange,
  partColors,
  onPartColorChange,
}) => {
  const togglePart = (partId: string) => {
    onPartsChange(
      selectedParts.includes(partId)
        ? selectedParts.filter((p) => p !== partId)
        : [...selectedParts, partId]
    );
  };

  const [open, setOpen] = useState(false);

  return (
    <div className="h-full flex flex-col">
      
      {/* ✅ STICKY HEADER (Mobile Only) */}
      <div className="sticky top-0 z-10 bg-white px-6 py-4 border-b lg:static lg:border-none">
        <h2 className="text-2xl lg:text-3xl font-semibold">
          {product.name}
        </h2>
      </div>

      {/* ✅ SCROLLABLE CONTENT */}
      <div className="flex-1 overflow-y-auto px-6 py-6 space-y-8 overscroll-contain">
        
        {/* PARTS */}
        <section>
          <h4 className="text-xs uppercase tracking-widest text-gray-500 mb-2">
            Parts
          </h4>
          <p className="text-xs text-gray-400 mb-4">
            Toggle visibility and assign colors
          </p>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-8">
            
            {/* LEFT */}
            <div className="flex flex-col">
              <div className="flex flex-col gap-3 min-h-[64px]">
                <button
                  onClick={() => togglePart("V4A")}
                  className="flex items-center gap-3 text-left"
                >
                  {selectedParts.includes("V4A") ? (
                    <Eye className="w-5 h-5 text-primary" />
                  ) : (
                    <EyeOff className="w-5 h-5 text-gray-400" />
                  )}
                  <span className="text-sm text-gray-700">Part A</span>
                </button>

                <button
                  onClick={() => togglePart("V4B")}
                  className="flex items-center gap-3 text-left"
                >
                  {selectedParts.includes("V4B") ? (
                    <Eye className="w-5 h-5 text-primary" />
                  ) : (
                    <EyeOff className="w-5 h-5 text-gray-400" />
                  )}
                  <span className="text-sm text-gray-700">Part B</span>
                </button>
              </div>

              <div className="flex flex-col gap-1 mt-6">
                <label className="text-[11px] uppercase tracking-wide text-gray-400">
                  Color – Part A & B
                </label>
                <select
                  value={partColors.AB}
                  onChange={(e) =>
                    onPartColorChange("AB", e.target.value)
                  }
                  className="w-full border border-gray-200 rounded-lg px-3 py-2 text-sm
                             focus:outline-none focus:ring-2 focus:ring-primary/20"
                >
                  {PART_COLORS.map((c) => (
                    <option key={c.value} value={c.value}>
                      {c.label}
                    </option>
                  ))}
                </select>
              </div>
            </div>

            {/* RIGHT */}
            <div className="flex flex-col">
              <div className="flex flex-col gap-3 min-h-[64px]">
                <button
                  onClick={() => togglePart("V4_Orings")}
                  className="flex items-center gap-3 text-left"
                >
                  {selectedParts.includes("V4_Orings") ? (
                    <Eye className="w-5 h-5 text-primary" />
                  ) : (
                    <EyeOff className="w-5 h-5 text-gray-400" />
                  )}
                  <span className="text-sm text-gray-700">O-rings</span>
                </button>
              </div>

              <div className="flex flex-col gap-1 mt-6">
                <label className="text-[11px] uppercase tracking-wide text-gray-400">
                  Color – Part ORINGS
                </label>
                <select
                  value={partColors.ORINGS}
                  onChange={(e) =>
                    onPartColorChange("ORINGS", e.target.value)
                  }
                  className="w-full border border-gray-200 rounded-lg px-3 py-2 text-sm
                             focus:outline-none focus:ring-2 focus:ring-primary/20"
                >
                  {PART_COLORS.map((c) => (
                    <option key={c.value} value={c.value}>
                      {c.label}
                    </option>
                  ))}
                </select>
              </div>
            </div>
          </div>
        </section>

        {/* TEXT */}
        <p className="text-[15px] leading-relaxed text-gray-500">
          This visualization is a 3D render for demonstration purposes only.
          Actual product specifications, colors, and structure may vary.
        </p>
      </div>

      {/* ✅ STICKY FOOTER BUTTON (Mobile Only) */}
      <div className="sticky bottom-0 z-10 bg-white p-4 border-t lg:static lg:border-none">
        <button
          onClick={() => setOpen(true)}
          className="w-full lg:w-auto px-6 py-3 rounded-xl bg-primary text-white text-sm font-semibold
                     hover:shadow-xl hover:shadow-primary/30 transition-all"
        >
          Contact us
        </button>
      </div>

      {/* MODAL */}
      <ContactModal open={open} onClose={() => setOpen(false)} />
    </div>
  );
};

export default ProductInfo;